// src/components/MentionInput.tsx

import React, { useRef, useState, useEffect } from 'react';
import {
    Box,
    Popper,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';

interface AppUser {
    email: string;
}

interface MentionInputProps {
    value: string;
    onChange: (html: string) => void;
    onSubmit: () => void;
    onCancel: () => void;
    allUsers: AppUser[];
    autoFocus?: boolean;
}

const MentionInput: React.FC<MentionInputProps> = ({
    value,
    onChange,
    onSubmit,
    onCancel,
    allUsers,
    autoFocus = false,
}) => {
    const theme = useTheme();
    const editorRef = useRef<HTMLDivElement>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const filteredUsers = allUsers.filter((user) =>
        user.email.toLowerCase().includes(mentionQuery.toLowerCase())
    );

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;

            // Move caret to end of content
            const range = document.createRange();
            range.selectNodeContents(editorRef.current);
            range.collapse(false);

            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        }
    }, [value]);

    useEffect(() => {
        if (autoFocus) {
            if (isMobile) {
                setTimeout(() => {
                    requestAnimationFrame(() => editorRef.current?.focus());
                }, 100);
            } else {
                setTimeout(() => editorRef.current?.focus(), 0);
            }
        }
    }, [autoFocus]);

    const insertMention = (name: string) => {
        const mentionSpan = document.createElement('span');
        mentionSpan.contentEditable = 'false';
        mentionSpan.className = 'mention-pill';
        mentionSpan.innerText = `@${name}`;
        mentionSpan.setAttribute('data-mention', name);

        const space = document.createTextNode(' ');

        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        const match = range.startContainer.textContent
            ?.slice(0, range.startOffset)
            .match(/@[\w\d_-]*$/);
        if (match) {
            const startOffset = range.startOffset - match[0].length;
            range.setStart(range.startContainer, startOffset);
            range.deleteContents();
            range.insertNode(space);
            range.insertNode(mentionSpan);
            range.setStartAfter(space);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
        }

        setMentionQuery('');
        setShowSuggestions(false);
        if (isMobile) {
            setTimeout(() => {
                requestAnimationFrame(() => editorRef.current?.focus());
            }, 100);
        } else {
            editorRef.current?.focus();
        }
        onChange(editorRef.current?.innerHTML || '');
    };

    const handleInput = () => {
        const html = editorRef.current?.innerHTML || '';
        onChange(html);

        const sel = window.getSelection();
        if (!sel || !sel.focusNode) return;

        const text = sel.focusNode.textContent?.slice(0, sel.focusOffset) || '';
        const match = text.match(/@([\w\d_-]*)$/);
        if (match) {
            setMentionQuery(match[1]);
            setShowSuggestions(true);
            setAnchorEl(editorRef.current);
        } else {
            setMentionQuery('');
            setShowSuggestions(false);
        }

        setHighlightedIndex(0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (showSuggestions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    prev < filteredUsers.length - 1 ? prev + 1 : prev
                );
                return;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                return;
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const name = filteredUsers[highlightedIndex].email.replace(
                    '@valaratomics.com',
                    ''
                );
                insertMention(name);
                return;
            }
        }

        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
        } else if (e.key === 'Backspace') {
            const sel = window.getSelection();
            const node = sel?.focusNode;
            const offset = sel?.focusOffset ?? 0;

            if (node?.nodeType === Node.TEXT_NODE && offset === 0) {
                const prev = node.previousSibling;
                if (
                    prev?.nodeType === Node.ELEMENT_NODE &&
                    (prev as HTMLElement).classList.contains('mention-pill')
                ) {
                    e.preventDefault();
                    prev.remove();
                    onChange(editorRef.current?.innerHTML || '');
                }
            }
        }
    };

    return (
        <>
            <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text/plain');
                    document.execCommand('insertText', false, text);
                }}
                sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    padding: '12px 16px',
                    minHeight: '80px',
                    fontSize: '1rem',
                    lineHeight: 1.5,
                    backgroundColor: '#fdfdfd',
                    outline: 'none',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                    '& .mention-pill': {
                        backgroundColor: '#d1c4e9',
                        padding: '2px 6px',
                        borderRadius: '12px',
                        color: '#4a148c',
                        fontWeight: 500,
                        marginRight: '2px',
                    },
                }}
            />
            <Popper
                open={showSuggestions && filteredUsers.length > 0}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
            >
                <Paper
                    elevation={3}
                    sx={{ maxHeight: 200, overflowY: 'auto', width: 250 }}
                >
                    <List dense>
                        {filteredUsers.map((user, index) => {
                            const nameOnly = user.email.replace(
                                '@valaratomics.com',
                                ''
                            );
                            return (
                                <ListItem key={user.email} disablePadding>
                                    <ListItemButton
                                        selected={index === highlightedIndex}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            insertMention(nameOnly);
                                        }}
                                    >
                                        <ListItemText
                                            primary={`@${nameOnly}`}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Paper>
            </Popper>
        </>
    );
};

export default MentionInput;
